import React from 'react'
import { ReactComponentLike } from 'prop-types'
import { NodeRenderer } from '@contentful/rich-text-react-renderer'
import {
  ContentfulRichTextGatsbyReference,
  renderRichText,
  RenderRichTextData,
} from 'gatsby-source-contentful/rich-text'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import slugify from '@sindresorhus/slugify'

import {
  RTFBlockOptions,
  RTFBlockComponentExport,
  RTFPossibleOptions,
  TitleResolver,
} from '../types'
import ParagraphBlock from '../content-blocks/paragraph'
import { ListItem, OrderedList, UnorderedList } from '../content-blocks/list'
import BlockquoteBlock from '../content-blocks/blockquote'
import WithDocumentOutlineSection from '../with-document-outline-section'

interface BlockMap {
  [blockName: string]: RTFBlockComponentExport
}

interface RTFComponentProps<
  TReference extends ContentfulRichTextGatsbyReference
> {
  data: RenderRichTextData<TReference>
  options?: RTFBlockOptions
}

export default function generateRTFComponent(
  blockMap: BlockMap = {},
  resolveTitle?: TitleResolver
) {
  return function RTFComponent<T extends ContentfulRichTextGatsbyReference>({
    data,
    options = {},
  }: RTFComponentProps<T>) {
    let index = 0

    const renderReferenceBlock: NodeRenderer = (node, children) => {
      const blockIndex = index++
      if (!node.data.target) {
        // We don't have data for this block. It was probably not queried in the `references`. We
        // can't render it. So, skip it.
        return null
      }
      const { __typename: blockType, ...props } = node.data.target
      const blockData = blockMap[blockType]
      if (!blockData) return null
      const { component: Component, isValid } = blockData
      const blockOptions = options[blockType] as RTFPossibleOptions
      return isValid(props) ? (
        <Component
          key={`${blockType}-${blockIndex}`}
          blockIndex={blockIndex}
          options={blockOptions || {}}
          {...props}
        >
          {children}
        </Component>
      ) : null
    }

    const renderContentBlock = (
      blockType: string,
      Component: ReactComponentLike
    ): NodeRenderer =>
      function RTFContentBlock(_node, children) {
        const blockIndex = index++
        const blockOptions = options[blockType] as RTFPossibleOptions
        return (
          <Component
            key={`${blockType}-${blockIndex}`}
            blockIndex={blockIndex}
            options={blockOptions || {}}
          >
            {children}
          </Component>
        )
      }

    const renderHeadingBlock = (headingRank: number): NodeRenderer => (
      node,
      children
    ) => {
      const blockIndex = index++
      // This heading element is actually not being used in the end. It's to make sure that we're
      // returning a React element but we're just piggy-backing additional information with it
      // which is then picked up by `<WithDocumentOutlineSection>` to replace these headings with
      // `<DocumentOutlineSection>` / `<H>`
      const TagName = `h${headingRank}` as React.ElementType
      const headingNode = (
        <TagName key={`HeadingBlock-${blockIndex}`}>{children}</TagName>
      )
      // Now, we're assembling the additional information
      const content = node.content[0]
      const slugified =
        content.nodeType === 'text' ? slugify(content.value) : ''
      // And this is where it get's interesting: Here we're returning basically a React element,
      // but we've added our additional information to it!
      return {
        ...headingNode,
        blockType: 'HeadingBlock',
        slugified,
        headingRank,
      }
    }

    const renderedRichText = renderRichText(data, {
      renderNode: {
        [BLOCKS.PARAGRAPH]: renderContentBlock(
          'ParagraphBlock',
          ParagraphBlock
        ),
        [BLOCKS.UL_LIST]: renderContentBlock('ListBlock', UnorderedList),
        [BLOCKS.OL_LIST]: renderContentBlock('ListBlock', OrderedList),
        [BLOCKS.LIST_ITEM]: renderContentBlock('ListBlock', ListItem),
        [BLOCKS.QUOTE]: renderContentBlock('BlockquoteBlock', BlockquoteBlock),
        [BLOCKS.HEADING_1]: renderHeadingBlock(1),
        [BLOCKS.HEADING_2]: renderHeadingBlock(2),
        [BLOCKS.HEADING_3]: renderHeadingBlock(3),
        [BLOCKS.HEADING_4]: renderHeadingBlock(4),
        [BLOCKS.HEADING_5]: renderHeadingBlock(5),
        [BLOCKS.HEADING_6]: renderHeadingBlock(6),
        [BLOCKS.EMBEDDED_ASSET]: renderReferenceBlock,
        [BLOCKS.EMBEDDED_ENTRY]: renderReferenceBlock,
        [INLINES.HYPERLINK]: function RTFHyperlink(node, children) {
          const uri = node.data.uri
          const href = uri.replace(/https?:\/\/8fit.com\//, '/')
          if (!href.startsWith('/')) {
            // External link. Add `noopener noreferrer`
            return (
              <a href={uri} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            )
          }
          return <a href={href}>{children}</a>
        },
        [INLINES.ASSET_HYPERLINK]: function RTFAssetHyperlink(node, children) {
          const props = node.data.target
          const { url } = props.file
          return (
            <a href={url} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          )
        },
      },
    })

    return (
      <WithDocumentOutlineSection
        nodes={renderedRichText}
        resolveTitle={resolveTitle}
      />
    )
  }
}
